import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useAdmin";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { Search, Download, Package, Mail } from "lucide-react";

interface MaterialRequest {
  id: string;
  email: string;
  material_description: string;
  gdpr_consent_at: string;
  created_at: string;
  is_active: boolean;
}

function useMaterialRequests() {
  return useQuery({
    queryKey: ["material-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("material_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as MaterialRequest[];
    },
  });
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminMaterialRequests() {
  const { user, loading: authLoading } = useAuth();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: requests, isLoading: requestsLoading } = useMaterialRequests();
  const [searchQuery, setSearchQuery] = useState("");

  if (authLoading || adminLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <h1 className="mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin access.</p>
        </div>
      </Layout>
    );
  }

  const filtered = requests?.filter((r) => {
    const q = searchQuery.toLowerCase();
    return r.email.toLowerCase().includes(q) || r.material_description.toLowerCase().includes(q);
  });

  const exportToCSV = () => {
    if (!filtered?.length) return;
    const headers = ["Email", "Material Request", "Date"];
    const rows = filtered.map((r) => [r.email, r.material_description, formatDate(r.created_at)]);
    const csv = [headers.join(","), ...rows.map((row) => row.map((c) => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `material-requests-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  return (
    <Layout>
      <div className="container mx-auto px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="mb-2">Material Requests</h1>
            <p className="text-muted-foreground">View incoming material search requests</p>
          </div>
          <Button onClick={exportToCSV} disabled={!filtered?.length}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{requests?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {new Set(requests?.map((r) => r.email)).size || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Unique Emails</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Requests ({filtered?.length || 0})
              </CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search email or material..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {requestsLoading ? (
              <p className="text-center text-muted-foreground py-8">Loading...</p>
            ) : filtered?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {searchQuery ? "No requests match your search." : "No requests yet."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Material Request</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered?.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.email}</TableCell>
                        <TableCell className="max-w-xs truncate">{r.material_description}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(r.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
